<script setup>
// The hero graphic on /features: every area of the product as an icon,
// orbiting the Tap Leagues mark on two slow rings. Pure CSS, so it is crisp
// at any size and weighs nothing. The rings turn and each icon counter-turns,
// so the icons stay upright. Still under prefers-reduced-motion.
import {FEATURE_SECTIONS} from '~/utils/features';
import {formatExplainers} from '~/utils/formatExplainers';

// The five formats keep their own identity colours (the same pairs as their
// crests and cards everywhere else). The other areas each take a different
// hue from a palette that stays clear of those five and of brand red.
const FORMAT_OF_SECTION = {leagues: 'league', ladders: 'ladder', tournaments: 'tournament', americano: 'americano', meets: 'meet'};
const PALETTE = [
  {ink: '#0F766E', tint: '#E0F2F0'}, // teal
  {ink: '#4F46E5', tint: '#E8E7FB'}, // indigo
  {ink: '#DB2777', tint: '#FBE4EF'}, // pink
  {ink: '#EA580C', tint: '#FDE9DF'}, // orange
  {ink: '#0891B2', tint: '#E0F4F8'}, // cyan
  {ink: '#65A30D', tint: '#ECF5DF'}, // lime
  {ink: '#475569', tint: '#E8ECF1'}, // slate
  {ink: '#B45309', tint: '#F8EBDC'}, // bronze
  {ink: '#0E7490', tint: '#DFF1F5'}, // deep cyan
  {ink: '#9D174D', tint: '#F8E1EB'}, // raspberry
  {ink: '#3F6212', tint: '#E8F0DC'}, // olive
];
let next = 0;
const toneOf = (section) => {
  const format = formatExplainers[FORMAT_OF_SECTION[section.key]];
  if (format) return {ink: format.inkHex, tint: format.tintHex};
  return PALETTE[next++ % PALETTE.length];
};

const icons = FEATURE_SECTIONS.map((section) => ({
  key: section.key,
  icon: section.icon,
  title: section.title,
  ...toneOf(section),
}));
// Six on the inner ring, the rest on the outer
const inner = icons.slice(0, 6);
const outer = icons.slice(6);
const angle = (i, n) => (360 / n) * i;
</script>

<template>
  <div class="fio" aria-hidden="true">
    <div class="fio-glow"/>
    <div class="fio-ring fio-ring--outer">
      <span
          v-for="(item, i) in outer"
          :key="item.key"
          class="fio-seat"
          :style="{'--a': angle(i, outer.length) + 'deg'}">
        <span class="fio-icon" :style="{color: item.ink, background: item.tint}" :title="item.title">
          <v-icon size="22" :icon="item.icon"/>
        </span>
      </span>
    </div>
    <div class="fio-ring fio-ring--inner">
      <span
          v-for="(item, i) in inner"
          :key="item.key"
          class="fio-seat"
          :style="{'--a': angle(i, inner.length) + 'deg'}">
        <span class="fio-icon fio-icon--lg" :style="{color: item.ink, background: item.tint}" :title="item.title">
          <v-icon size="26" :icon="item.icon"/>
        </span>
      </span>
    </div>
    <div class="fio-core">
      <img src="/images/icon_red.png" alt="" width="96" height="96">
    </div>
  </div>
</template>

<style scoped lang="scss">
/* The graphic is a square; --r1 and --r2 are the ring radii */
.fio {
  --size: 300px;
  --r1: 92px;
  --r2: 148px;
  position: relative;
  width: var(--size);
  height: var(--size);
  margin: 0 auto;

  @media (min-width: 600px) {
    --size: 380px;
    --r1: 116px;
    --r2: 186px;
  }

  @media (min-width: 960px) {
    --size: 440px;
    --r1: 132px;
    --r2: 214px;
  }
}

.fio-glow {
  position: absolute;
  inset: 8%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(211, 41, 70, 0.14), rgba(211, 41, 70, 0.04) 55%, transparent 72%);
}

.fio-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: fio-turn linear infinite;

  /* the faint track */
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: calc(var(--r) * 2);
    height: calc(var(--r) * 2);
    margin: calc(var(--r) * -1) 0 0 calc(var(--r) * -1);
    border-radius: 50%;
    border: 1px dashed rgb(var(--v-theme-border));
  }

  &--inner {
    --r: var(--r1);
    animation-duration: 70s;
  }

  &--outer {
    --r: var(--r2);
    animation-duration: 110s;
    animation-direction: reverse;
  }
}

.fio-seat {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 0;
  transform: rotate(var(--a)) translateY(calc(var(--r) * -1));
}

.fio-icon {
  position: absolute;
  left: -22px;
  top: -22px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  box-shadow: 0 10px 24px -12px rgba(26, 26, 26, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.9) inset;
  /* keep upright while the ring turns */
  transform: rotate(calc(var(--a) * -1));
  animation: fio-counter linear infinite;

  &--lg {
    left: -26px;
    top: -26px;
    width: 52px;
    height: 52px;
    border-radius: 16px;
  }
}

/* inner ring turns forward, so its icons counter-turn backward; the outer ring
   runs in reverse, so its icons counter-turn forward */
.fio-ring--inner .fio-icon {
  animation-name: fio-counter;
  animation-duration: 70s;
}

.fio-ring--outer .fio-icon {
  animation-name: fio-counter-rev;
  animation-duration: 110s;
}

.fio-core {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 96px;
  height: 96px;
  margin: -48px 0 0 -48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 20px 40px -20px rgba(163, 28, 53, 0.6);

  /* The app icon fills the circle edge to edge */
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

@keyframes fio-turn {
  to { transform: rotate(360deg); }
}

/* The seat's own rotation is static; the counter-turn undoes the ring's turn */
@keyframes fio-counter {
  from { transform: rotate(calc(var(--a) * -1)); }
  to { transform: rotate(calc(var(--a) * -1 - 360deg)); }
}

@keyframes fio-counter-rev {
  from { transform: rotate(calc(var(--a) * -1)); }
  to { transform: rotate(calc(var(--a) * -1 + 360deg)); }
}

@media (prefers-reduced-motion: reduce) {
  .fio-ring,
  .fio-icon {
    animation: none;
  }
}
</style>
